import { titles, currentContext } from './session.js';
import { openBackdrop, closeBackdrop } from './ui/modal.js';
import { buildDropdown } from './ui/dropdown.js';
import { escapeHtml } from './util.js';
import { activatePreview } from './preview.js';

const selectEls = {
  step: document.getElementById('selectStep'),
  head: document.getElementById('selectTitleHead'),
  intro: document.getElementById('selectIntro'),
  body: document.getElementById('selectBody'),
  breadcrumb: document.getElementById('selectBreadcrumb'),
  back: document.getElementById('selectBack'),
};

let selectState = null;

// Count helpers for the 4-level tree.
function creativeCanvasCount(c) {
  return (c.files || []).reduce((sum, f) => sum + (f.canvases ? f.canvases.length : 0), 0);
}
function titleCanvasCount(t) {
  return t.creatives.reduce((sum, c) => sum + creativeCanvasCount(c), 0);
}

export function openSelectModal() {
  // Check if a preview is currently active with a valid title context
  const hasContext = currentContext.titleId && titles.find(t => t.id === currentContext.titleId);

  if (hasContext) {
    // Combined step — title + creative in one screen, with title pre-selected
    const title = titles.find(t => t.id === currentContext.titleId);
    selectState = {
      step: 'combined',
      titleId: title.id,
      titleName: title.name,
      creativeId: null, creativeName: null,
      fileIndex: null, fileObj: null,
      canvas: null,
      enteredFromContext: true,
    };
  } else {
    // Normal entry — stacked picker
    selectState = {
      step: 1,
      titleId: null, titleName: null,
      creativeId: null, creativeName: null,
      fileIndex: null, fileObj: null,
      canvas: null,
      enteredFromContext: false,
    };
  }
  openBackdrop('selectModal');
  renderSelectModal();
}

function renderSelectModal() {
  const s = selectState;

  // ===== Combined step (entered from preview context) =====
  if (s.step === 'combined') {
    selectEls.step.textContent = 'QUICK SWITCH · 타이틀 + 소재';
    selectEls.head.textContent = '다른 소재로 전환';
    selectEls.intro.textContent = '현재 작업 중인 타이틀입니다. 소재를 골라 미리보기를 교체합니다.';
    renderSelectBreadcrumb();
    selectEls.body.innerHTML = '';
    renderSelectCombinedPicker();
    selectEls.back.style.display = 'none';
    return;
  }

  // ===== Standard stacked flow (stacked accumulation) =====
  // title → 대분류(creative) → 소분류(file) → open file's first canvas
  const stepText = s.fileObj
    ? '완료'
    : s.creativeId
      ? '파일을 선택해주세요'
      : s.titleId
        ? '대분류를 선택해주세요'
        : '타이틀을 선택해주세요';
  selectEls.step.textContent = `소재 선택 · ${stepText}`;
  selectEls.head.textContent = '소재를 열어볼까요?';
  selectEls.intro.textContent = '타이틀부터 차례대로 선택하면 다음 단계가 자동으로 나타납니다.';
  renderSelectBreadcrumb();
  selectEls.body.innerHTML = '';
  renderSelectStackedPicker();
  selectEls.back.style.display = 'none';
}

function renderSelectBreadcrumb() {
  const s = selectState;
  const parts = [];
  if (s.titleName) parts.push(s.titleName);
  if (s.creativeName) parts.push(s.creativeName);
  if (s.fileObj) parts.push(s.fileObj.name);
  if (parts.length === 0) {
    selectEls.breadcrumb.innerHTML = '<span class="crumb">위치를 선택해주세요</span>';
  } else {
    selectEls.breadcrumb.innerHTML = parts.map(p =>
      `<span class="crumb">${escapeHtml(p)}</span>`
    ).join('<span class="arrow">→</span>');
  }
}

// ===== Stacked picker — title → 대분류 → 소분류 accumulate on one screen =====
function renderSelectStackedPicker() {
  const s = selectState;
  const wrap = document.createElement('div');
  wrap.className = 'stacked-picker';

  const titleIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`;
  const creativeIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>`;
  const fileIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;

  // ===== Section 1: Title =====
  const titleSection = document.createElement('div');
  titleSection.className = 'stack-section';
  const titleLabel = document.createElement('div');
  titleLabel.className = 'picker-section-label';
  titleLabel.textContent = '타이틀';
  titleSection.appendChild(titleLabel);
  titleSection.appendChild(buildDropdown({
    items: titles.map(t => ({
      id: t.id, name: t.name,
      meta: `${t.creatives.length}개 대분류 · ${titleCanvasCount(t)}개 캔버스`,
      icon: titleIcon,
    })),
    selectedId: s.titleId,
    placeholder: '타이틀을 선택하세요',
    searchPlaceholder: '타이틀 검색',
    triggerIcon: titleIcon,
    autoOpen: !s.titleId,    // first-time: auto open
    onSelect: (item) => {
      selectState.titleId = item.id;
      selectState.titleName = item.name;
      selectState.creativeId = null;
      selectState.creativeName = null;
      selectState.fileIndex = null;
      selectState.fileObj = null;
      renderSelectBreadcrumb();
      // Re-render to expose the next section; mark "newly revealed" so it auto-opens
      selectState._newlyRevealed = 'creative';
      renderSelectModal();
    },
  }));
  wrap.appendChild(titleSection);

  // ===== Section 2: 대분류(creative) — only if title is chosen =====
  if (s.titleId) {
    const t = titles.find(x => x.id === s.titleId);
    const creatives = t ? t.creatives : [];

    const creativeSection = document.createElement('div');
    creativeSection.className = 'stack-section' + (s._newlyRevealed === 'creative' ? ' newly-revealed' : '');

    const creativeLabel = document.createElement('div');
    creativeLabel.className = 'picker-section-label';
    creativeLabel.textContent = '대분류';
    creativeSection.appendChild(creativeLabel);

    if (creatives.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'picker-empty';
      empty.textContent = '이 타이틀에는 대분류가 없습니다.';
      creativeSection.appendChild(empty);
    } else {
      creativeSection.appendChild(buildDropdown({
        items: creatives.map(c => ({
          id: c.id, name: c.name,
          meta: `${(c.files || []).length}개 소분류 · ${creativeCanvasCount(c)}개 캔버스`,
          icon: creativeIcon,
        })),
        selectedId: s.creativeId,
        placeholder: '대분류를 선택하세요',
        searchPlaceholder: '대분류 검색',
        triggerIcon: creativeIcon,
        autoOpen: s._newlyRevealed === 'creative' && !s.creativeId,
        onSelect: (item) => {
          selectState.creativeId = item.id;
          selectState.creativeName = item.name;
          selectState.fileIndex = null;
          selectState.fileObj = null;
          renderSelectBreadcrumb();
          selectState._newlyRevealed = 'file';
          renderSelectModal();
        },
      }));
    }
    wrap.appendChild(creativeSection);
  }

  // ===== Section 3: 소분류(file) — only if creative is chosen =====
  if (s.titleId && s.creativeId) {
    const t = titles.find(x => x.id === s.titleId);
    const c = t ? t.creatives.find(x => x.id === s.creativeId) : null;
    const files = c ? (c.files || []) : [];

    const fileSection = document.createElement('div');
    fileSection.className = 'stack-section' + (s._newlyRevealed === 'file' ? ' newly-revealed' : '');

    const fileLabel = document.createElement('div');
    fileLabel.className = 'picker-section-label';
    fileLabel.textContent = '소분류';
    fileSection.appendChild(fileLabel);

    if (files.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'picker-empty';
      empty.textContent = '이 대분류에는 파일이 없습니다.';
      fileSection.appendChild(empty);
    } else {
      fileSection.appendChild(buildDropdown({
        items: files.map(f => ({
          id: f.id, name: f.name,
          meta: `${(f.canvases || []).length}개 캔버스`,
          icon: fileIcon,
        })),
        selectedId: s.fileObj ? s.fileObj.id : null,
        placeholder: '파일을 선택하세요',
        searchPlaceholder: '파일 검색',
        triggerIcon: fileIcon,
        autoOpen: s._newlyRevealed === 'file',
        onSelect: (item) => {
          const file = files.find(f => f.id === item.id);
          selectState.fileObj = file;
          renderSelectBreadcrumb();
          openFileDefault(file);
        },
      }));
    }
    wrap.appendChild(fileSection);
  }

  selectEls.body.appendChild(wrap);

  // Clear newly-revealed flag after this render (so re-renders due to other reasons don't auto-open again)
  s._newlyRevealed = null;
}

// ===== Combined picker — title + creative on one screen (preview context) =====
function renderSelectCombinedPicker() {
  const wrap = document.createElement('div');
  const s = selectState;

  const titleIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`;
  const creativeIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>`;
  const fileIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;

  // ===== Title section =====
  const titleLabel = document.createElement('div');
  titleLabel.className = 'picker-section-label';
  titleLabel.textContent = '타이틀';
  wrap.appendChild(titleLabel);

  wrap.appendChild(buildDropdown({
    items: titles.map(t => ({
      id: t.id, name: t.name,
      meta: `${t.creatives.length}개 대분류 · ${titleCanvasCount(t)}개 캔버스`,
      icon: titleIcon,
    })),
    selectedId: s.titleId,
    placeholder: '타이틀을 선택하세요',
    searchPlaceholder: '타이틀 검색',
    triggerIcon: titleIcon,
    onSelect: (item) => {
      selectState.titleId = item.id;
      selectState.titleName = item.name;
      // Reset lower levels when title changes
      selectState.creativeId = null;
      selectState.creativeName = null;
      selectState.fileObj = null;
      renderSelectBreadcrumb();
      // Re-render combined picker only (no fade — title is just being updated)
      selectEls.body.innerHTML = '';
      renderSelectCombinedPicker();
    },
  }));

  // ===== 대분류(creative) section =====
  const creativeLabel = document.createElement('div');
  creativeLabel.className = 'picker-section-label';
  creativeLabel.textContent = '대분류';
  creativeLabel.style.marginTop = '18px';
  wrap.appendChild(creativeLabel);

  const title = titles.find(t => t.id === s.titleId);
  const creatives = title ? title.creatives : [];

  if (creatives.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'picker-empty';
    empty.textContent = '이 타이틀에는 대분류가 없습니다.';
    wrap.appendChild(empty);
  } else {
    wrap.appendChild(buildDropdown({
      items: creatives.map(c => ({
        id: c.id, name: c.name,
        meta: `${(c.files || []).length}개 소분류 · ${creativeCanvasCount(c)}개 캔버스`,
        icon: creativeIcon,
      })),
      selectedId: s.creativeId,
      placeholder: '대분류를 선택하세요',
      searchPlaceholder: '대분류 검색',
      triggerIcon: creativeIcon,
      onSelect: (item) => {
        selectState.creativeId = item.id;
        selectState.creativeName = item.name;
        selectState.fileObj = null;
        renderSelectBreadcrumb();
        // Re-render combined picker to reveal the 소분류 section
        selectEls.body.innerHTML = '';
        renderSelectCombinedPicker();
      },
    }));
  }

  // ===== 소분류(file) section — only if a creative is chosen =====
  if (s.creativeId) {
    const c = title ? title.creatives.find(x => x.id === s.creativeId) : null;
    const files = c ? (c.files || []) : [];

    const fileLabel = document.createElement('div');
    fileLabel.className = 'picker-section-label';
    fileLabel.textContent = '소분류';
    fileLabel.style.marginTop = '18px';
    wrap.appendChild(fileLabel);

    if (files.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'picker-empty';
      empty.textContent = '이 대분류에는 파일이 없습니다.';
      wrap.appendChild(empty);
    } else {
      wrap.appendChild(buildDropdown({
        items: files.map(f => ({
          id: f.id, name: f.name,
          meta: `${(f.canvases || []).length}개 캔버스`,
          icon: fileIcon,
        })),
        selectedId: s.fileObj ? s.fileObj.id : null,
        placeholder: '파일을 선택하세요',
        searchPlaceholder: '파일 검색',
        triggerIcon: fileIcon,
        onSelect: (item) => {
          const file = files.find(f => f.id === item.id);
          selectState.fileObj = file;
          // Quick switch: open the file's first canvas directly.
          openFileDefault(file);
        },
      }));
    }
  }

  selectEls.body.appendChild(wrap);
}

// Open a file's first canvas. Used by both pickers — canvas switching itself
// happens via the preview top tab, not here.
function openFileDefault(file) {
  const s = selectState;
  if (!file) return;
  const canvases = file.canvases || [];
  if (!canvases.length) return;
  const cv = canvases[0];
  closeBackdrop('selectModal');
  activatePreview({
    fileName: cv.name,
    path: `${s.titleName} / ${s.creativeName} / ${file.name} / ${cv.name}`,
    canvas: cv,
    titleId: s.titleId,
    creativeId: s.creativeId,
    fileId: file.id,
  });
}

